// src/hooks/useProductInputs.js
import { useMemo } from "react";
import { getProductByCode } from "../constants/products";
import { getFontsByProductCode } from "../constants/fonts";
import { getColorsByProductCode } from "../constants/colors";
import { getHardwareByProductCode } from "../constants/hardwares";
import { getFinishByProductCode } from "../constants/finishes";
import { getThicknessByProductCode } from "../constants/thicknesses";

export const useProductInputs = (productCode) => {
  return useMemo(() => {
    if (!productCode) return { inputs: [], defaults: {} };

    const product = getProductByCode(productCode);
    if (!product) return { inputs: [], defaults: {} };

    const inputs = [];
    const defaults = {};

    // === TEXT (always first) ===
    inputs.push({
      key: "text",
      type: "text",
      label: "Text",
      placeholder: "Enter text...",
    });
    defaults.text = "";

    // === DYNAMIC ATTRIBUTES FROM product_attributes ===
    const attrs = product.product_attributes || {};

    // SIZE
    if (attrs.size) {
      const sizeOptions =
        product.sizes?.map((s) => ({
          value: s.code,
          label: s.name,
        })) || [];
      if (sizeOptions.length > 0) {
        inputs.push({
          key: "size",
          type: "select",
          label: "Size",
          options: sizeOptions,
        });
        defaults.size = sizeOptions[0].value;
      }
    }

    // COLOR
    if (attrs.color) {
      const colors = getColorsByProductCode(productCode) || [];
      if (colors.length > 0) {
        inputs.push({
          key: "color",
          type: "select",
          label: "Color",
          options: colors.map((c) => ({
            value: c.mivaname || c.code,
            label: c.name || c.label,
            color: c.hex, // for swatch
          })),
        });
        defaults.color = colors[0].mivaname || colors[0].code;
      }
    }

    // FONT
    if (attrs.font) {
      const fonts = getFontsByProductCode(productCode) || [];
      if (fonts.length > 0) {
        inputs.push({
          key: "font",
          type: "select",
          label: "Font",
          options: fonts.map((f) => ({
            name: f.name,
            code: f.code,
            value: f.code,
            label: f.name,
          })),
        });
        defaults.font = fonts[0].code;
      }
    }

    // HARDWARE
    if (attrs.hardware) {
      const hardware = getHardwareByProductCode(productCode) || [];
      if (hardware.length > 0) {
        inputs.push({
          key: "hardware",
          type: "select",
          label: "Mounting",
          options: hardware.map((h) => ({
            value: h.code,
            label: h.name,
            src: h.src, // for image rendering in LineRow
          })),
        });
        defaults.hardware = hardware[0].code;
      }
    }

    // FINISH (e.g. BL2200, Cast Metal)
    if (attrs.finish) {
      const finishes = getFinishByProductCode(productCode) || [];
      if (finishes.length > 0) {
        inputs.push({
          key: "finish",
          type: "select",
          label: "Finish",
          options: finishes.map((f) => ({
            value: f.code,
            label: f.name,
          })),
        });
        defaults.finish = finishes[0].code;
      }
    }

    // THICKNESS (e.g. acrylic, some plastic)
    if (attrs.thickness) {
      const thicknesses = getThicknessByProductCode(productCode) || [];
      if (thicknesses.length > 0) {
        inputs.push({
          key: "thickness",
          type: "select",
          label: "Thickness",
          options: thicknesses.map((t) => ({
            value: t.code,
            label: t.name,
          })),
        });
        defaults.thickness = thicknesses[0].code;
      }
    }

    return { inputs, defaults };
  }, [productCode]);
};
